import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCustomerContacts } from '@/hooks/useCustomerContacts';
import { useMode } from '@/providers/ModeProvider';

const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'Dr.', 'Prof.', 'Rev.'];
const PREFERRED_CONTACT_METHOD_OPTIONS = [
  'Email',
  'SMS',
  'Phone',
  'Customer Portal',
];

interface AddContactDialogProps {
  customerId: string;
  defaultCompany?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactCreated?: (contactId: string) => void;
}

export function AddContactDialog({
  customerId,
  defaultCompany,
  open,
  onOpenChange,
  onContactCreated,
}: AddContactDialogProps) {
  const { createContact } = useCustomerContacts(customerId);
  const { isDemo } = useMode();

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    surname: '',
    email: '',
    phone: '',
    mobile: '',
    preferredContactMethod: '',
    jobTitle: '',
    company: defaultCompany ?? '',
    notes: '',
    isPrimary: false,
  });

  useEffect(() => {
    if (open) {
      setFormData((prev) => ({ ...prev, company: defaultCompany ?? '' }));
    }
  }, [open, defaultCompany]);

  const resetForm = () => {
    setFormData({
      title: '',
      firstName: '',
      surname: '',
      email: '',
      phone: '',
      mobile: '',
      preferredContactMethod: '',
      jobTitle: '',
      company: defaultCompany ?? '',
      notes: '',
      isPrimary: false,
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async () => {
    if (
      !formData.firstName ||
      !formData.surname ||
      !formData.email ||
      !formData.phone
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const phoneRegex = /^[0-9\s\-+().]+$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid phone number (no letters allowed)');
      return;
    }

    if (formData.mobile && !phoneRegex.test(formData.mobile)) {
      toast.error('Please enter a valid mobile number (no letters allowed)');
      return;
    }

    if (isDemo) {
      toast.info('Contact creation is only available in Live mode');
      return;
    }

    try {
      const result = await createContact({
        title: formData.title || undefined,
        firstName: formData.firstName,
        surname: formData.surname,
        email: formData.email,
        phone: formData.phone,
        mobile: formData.mobile || undefined,
        preferredContactMethod: formData.preferredContactMethod || undefined,
        jobTitle: formData.jobTitle,
        company: formData.company,
        notes: formData.notes,
        isPrimary: formData.isPrimary,
      });

      if (!result?.id) {
        toast.error('Contact creation failed');
        return;
      }

      toast.success('Contact added successfully');
      resetForm();
      onOpenChange(false);
      onContactCreated?.(result.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to create contact',
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          <DialogDescription>
            Add a new contact. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-contact-title">Title</Label>
              <Select
                value={formData.title}
                onValueChange={(value) =>
                  setFormData({ ...formData, title: value })
                }
              >
                <SelectTrigger id="add-contact-title">
                  <SelectValue placeholder="Select title" />
                </SelectTrigger>
                <SelectContent>
                  {TITLE_OPTIONS.map((title) => (
                    <SelectItem key={title} value={title}>
                      {title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-firstName">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-contact-firstName"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                placeholder="John"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-surname">
                Surname <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-contact-surname"
                value={formData.surname}
                onChange={(e) =>
                  setFormData({ ...formData, surname: e.target.value })
                }
                placeholder="Smith"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-contact-email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-contact-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="john.smith@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-phone">
                Phone <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-contact-phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="+44 20 7946 0301"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="add-contact-mobile">Mobile</Label>
              <Input
                id="add-contact-mobile"
                value={formData.mobile}
                onChange={(e) =>
                  setFormData({ ...formData, mobile: e.target.value })
                }
                placeholder="+44 20 7946 0399"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-contact-preferredMethod">
                Preferred Contact Method
              </Label>
              <Select
                value={formData.preferredContactMethod}
                onValueChange={(value) =>
                  setFormData({ ...formData, preferredContactMethod: value })
                }
              >
                <SelectTrigger id="add-contact-preferredMethod">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {PREFERRED_CONTACT_METHOD_OPTIONS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-jobTitle">Job Title</Label>
            <Input
              id="add-contact-jobTitle"
              value={formData.jobTitle}
              onChange={(e) =>
                setFormData({ ...formData, jobTitle: e.target.value })
              }
              placeholder="Account Manager"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-company">Company</Label>
            <Input
              id="add-contact-company"
              value={formData.company}
              onChange={(e) =>
                setFormData({ ...formData, company: e.target.value })
              }
              placeholder="Company Name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="add-contact-notes">Notes</Label>
            <Textarea
              id="add-contact-notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Additional notes about this contact..."
              rows={3}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="add-contact-primary"
              checked={formData.isPrimary}
              onChange={(e) =>
                setFormData({ ...formData, isPrimary: e.target.checked })
              }
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="add-contact-primary" className="cursor-pointer">
              Set as primary contact
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Add Contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
